import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Star, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { reviews as reviewsApi } from '@/lib/api';
import { toast } from 'sonner';

export default function ProductReviewForm({ productId, onSubmitted }) {
  const { t } = useTranslation();
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating < 1) {
      toast.error(t('product.reviewRatingRequired', 'Lütfen puan seçin'));
      return;
    }
    if (!comment.trim()) {
      toast.error(t('product.reviewCommentRequired', 'Lütfen yorum yazın'));
      return;
    }
    setSubmitting(true);
    try {
      await reviewsApi.create({
        product_id: productId,
        rating,
        title: title.trim() || undefined,
        comment: comment.trim(),
      });
      toast.success(t('product.reviewSubmitted', 'Değerlendirmeniz kaydedildi'));
      setRating(0);
      setTitle('');
      setComment('');
      onSubmitted?.();
    } catch (err) {
      const msg = err.response?.data?.detail;
      if (msg === 'Already reviewed this product') {
        toast.error(t('product.reviewAlreadyExists', 'Bu ürünü zaten değerlendirdiniz'));
      } else {
        toast.error(msg || t('product.reviewSubmitError', 'Değerlendirme gönderilemedi'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const display = hoverRating || rating;

  return (
    <form onSubmit={handleSubmit} className="border rounded-lg p-4 mb-6 bg-muted/30 space-y-4" data-testid="product-review-form">
      <h3 className="text-sm font-semibold">{t('product.writeReview', 'Değerlendirme yaz')}</h3>
      <div>
        <Label className="text-xs mb-2 block">{t('product.yourRating', 'Puanınız')}</Label>
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              className="p-0.5"
              onMouseEnter={() => setHoverRating(star)}
              onMouseLeave={() => setHoverRating(0)}
              onClick={() => setRating(star)}
              aria-label={`${star} ${t('product.stars', 'yıldız')}`}
            >
              <Star
                className={`w-6 h-6 ${
                  star <= display ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
                }`}
              />
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="review-title" className="text-xs">{t('product.reviewTitle', 'Başlık (isteğe bağlı)')}</Label>
        <Input
          id="review-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t('product.reviewTitlePlaceholder', 'Kısa bir başlık')}
          className="h-9"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="review-comment" className="text-xs">{t('product.reviewComment', 'Yorumunuz')}</Label>
        <Textarea
          id="review-comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={3}
          placeholder={t('product.reviewCommentPlaceholder', 'Ürün hakkındaki deneyiminizi paylaşın...')}
          required
        />
      </div>
      <Button type="submit" size="sm" disabled={submitting}>
        {submitting && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
        {t('product.submitReview', 'Değerlendirmeyi gönder')}
      </Button>
    </form>
  );
}

export function ProductReviewLoginPrompt({ loginPath = '/login' }) {
  const { t } = useTranslation();
  return (
    <p className="text-sm text-muted-foreground text-center py-4 border rounded-lg mb-6 bg-muted/20">
      {t('product.reviewLoginRequired', 'Değerlendirme yapmak için')}{' '}
      <Link to={loginPath} className="text-primary font-medium hover:underline">
        {t('auth.login', 'giriş yapın')}
      </Link>
    </p>
  );
}
